import { action } from '@ember/object';
import type Owner from '@ember/owner';

import { PopupEvent } from 'webapp/enums/enums';

import type {
	Args as FormLeaveConfirmBaseArgs,
	FormLeaveConfirmBaseComponentInterface,
} from 'webapp/components/form/leave-confirm/base';
import FormLeaveConfirmBaseComponent from 'webapp/components/form/leave-confirm/base';

import type PopupContext from 'webapp/models-custom/popup/context';
import type PopupModal from 'webapp/models-custom/popup/modal';

// NOTE:
// This component is used specifically for checking leave confirm on popup close
// e.g. board/popup-visual-settings

interface Args extends FormLeaveConfirmBaseArgs {
	popup: PopupModal | PopupContext;
}

export default class FormLeaveConfirmPopupComponent extends FormLeaveConfirmBaseComponent<
	FormLeaveConfirmBaseComponentInterface<Args>
> {
	// possible to override discard function, so we execute that instead of closing popup
	// example: advanced DS settings tab switch
	specialDiscardFn: (() => void) | null = null;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		const popup = this.args.popup;
		popup.on(PopupEvent.CLOSE_INTENT, this, this.popupCloseIntentHandler);

		// prepare functions for teardown, since they dont have access to this.args
		this.teardownFns.push(() => {
			this.unbindPopupCloseIntentHandler(popup);
		});
	}

	@action
	popupCloseIntentHandler(args: unknown) {
		const isDirty = this.args.checkIsDirty();
		const specialDiscardFn = args as () => void | undefined;
		if (isDirty) {
			if (typeof specialDiscardFn === 'function') {
				this.specialDiscardFn = specialDiscardFn;
			} else {
				this.specialDiscardFn = null;
			}
			this.showLeaveConfirm = true;
		}
		return isDirty;
	}

	@action
	override triggerLeaveConfirm(): void {
		super.triggerLeaveConfirm();

		if (this.specialDiscardFn) {
			this.specialDiscardFn();
		} else {
			this.unbindPopupCloseIntentHandler(this.args.popup);
			void this.args.popup.close();
		}
	}

	@action
	override triggerSave(): void {
		super.triggerSave();
	}

	private teardownFns: (() => void)[] = [];

	@action
	override teardownView(): void {
		super.teardownView();
		this.teardownFns.forEach((fn) => fn());
	}

	private unbindPopupCloseIntentHandler(popup: PopupModal | PopupContext) {
		popup.off(PopupEvent.CLOSE_INTENT, this, this.popupCloseIntentHandler);
	}
}
